import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loginUser, getStudentDetails } from "../../lib/AuthApi";
import { useAuth } from "../../context/AuthProvider";
import Logo from "../../components/common/Logo";
import {
    ArrowRight,
    Eye,
    EyeOff,
    LockKeyhole,
    UserRound,
} from "lucide-react";

const TeacherLoginPage = () => {
    const navigate = useNavigate();
    const { setUserData } = useAuth();

    const [form, setForm] = useState({
        username: "",
        password: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [staySignedIn, setStaySignedIn] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // =========================================================
    // INPUT CHANGE
    // =========================================================
    const handleChange = (field, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (error) {
            setError("");
        }
    };

    // =========================================================
    // LOGIN
    // API LOGIC KEPT SAME
    // =========================================================
    const handleLogin = async (e) => {
        e.preventDefault();

        setError("");

        // =========================
        // FRONTEND VALIDATION
        // =========================
        if (!form.username.trim()) {
            setError(
                "Please enter username, email or mobile."
            );
            return;
        }

        if (!form.password.trim()) {
            setError(
                "Please enter your password."
            );
            return;
        }

        try {
            setLoading(true);

            // =========================
            // EXISTING LOGIN API
            // =========================
            const res = await loginUser({
                username: form.username.trim(),
                password: form.password,
            });

            console.log(
                "Teacher Login Response:",
                res
            );

            // =========================
            // API SUCCESS CHECK
            // =========================
            if (!res?.data?.isSuccess) {
                setError(
                    res?.message ||
                    "Invalid username or password."
                );
                return;
            }

            const result =
                res?.data?.result;

            // =========================
            // BASIC USER
            // =========================
            const basicUser = {
                token: result?.token,
                userID: result?.userID,
                uuid: result?.uuid,
                sessionId: result?.sessionId,
                role: result?.roleName,
            };

            // =========================
            // ROLE CHECK
            // =========================
            const role = String(
                result?.roleName ||
                result?.role ||
                ""
            ).toLowerCase();

            console.log(
                "Teacher Login Role:",
                role
            );

            if (
                role !== "teacher" &&
                role !== "admin"
            ) {
                setError(
                    "This account does not have teacher access."
                );
                return;
            }

            // =========================
            // SAVE BASIC USER
            // =========================
            localStorage.setItem(
                "user",
                JSON.stringify(basicUser)
            );

            setUserData(basicUser);

            // =========================
            // GET TEACHER DETAILS
            // EXISTING API
            // =========================
            try {
                const studentRes =
                    await getStudentDetails();

                console.log(
                    "Teacher Details Response:",
                    studentRes
                );

                const teacherData =
                    studentRes?.data?.[0] || {};

                const finalUserData = {
                    ...basicUser,
                    ...teacherData,
                    role:
                        teacherData?.role ||
                        basicUser.role,
                    hasSubscription: true,
                };

                // =========================
                // SAVE FINAL USER
                // =========================
                localStorage.setItem(
                    "user",
                    JSON.stringify(
                        finalUserData
                    )
                );

                setUserData(
                    finalUserData
                );
            } catch (detailsError) {
                // Details API fail hone par
                // login fail nahi hoga.
                console.error(
                    "Teacher details error:",
                    detailsError
                );
            }

            // =========================
            // EXISTING REDIRECT
            // =========================
            navigate("/live-classes");

        } catch (err) {
            console.error(
                "Teacher login error:",
                err
            );

            setError(
                err?.response?.data
                    ?.displayMessage ||
                err?.response?.data
                    ?.errorMessages?.[0] ||
                err?.message ||
                "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    // =========================================================
    // DEMO DETAILS
    // =========================================================
    const fillDemoDetails = () => {
        setForm({
            username: "demo",
            password: "demo123",
        });

        setError("");
    };

    return (
        <div className="min-h-screen w-full bg-[#F7F3EA] text-[#211C57] flex flex-col overflow-x-hidden">

            {/* =====================================================
                HEADER
            ===================================================== */}
            <header className="w-full">
                <div
                    className="
                        w-full
                        max-w-[1380px]
                        mx-auto
                        px-4
                        sm:px-6
                        md:px-8
                        lg:px-10
                        pt-6
                        sm:pt-8
                        lg:pt-9
                    "
                >
                    <Link
                        to="/"
                        aria-label="InstaED Tuitions home"
                        className="inline-flex flex-col items-start"
                    >
                        <Logo />
                    </Link>

                    <p
                        className="
                            mt-1
                            text-[10px]
                            sm:text-[11px]
                            md:text-xs
                            tracking-[0.18em]
                            uppercase
                            text-[#85809D]
                        "
                    >
                        Teacher access
                    </p>
                </div>
            </header>

            {/* =====================================================
                MAIN
            ===================================================== */}
            <main className="flex-1 w-full">

                <div
                    className="
                        w-full
                        max-w-[1380px]
                        mx-auto
                        px-4
                        sm:px-6
                        md:px-8
                        lg:px-10
                        py-8
                        sm:py-10
                        lg:py-14
                    "
                >

                    <section
                        className="
                            w-full
                            grid
                            grid-cols-1
                            lg:grid-cols-2
                            gap-8
                            md:gap-10
                            lg:gap-14
                            xl:gap-20
                            items-center
                            min-h-[520px]
                            lg:min-h-[610px]
                        "
                    >

                        {/* =================================================
                            LEFT CONTENT
                        ================================================= */}
                        <div
                            className="
                                w-full
                                max-w-[650px]
                                mx-auto
                                lg:mx-0
                                text-center
                                lg:text-left
                                order-2
                                lg:order-1
                            "
                        >

                            <div
                                className="
                                    inline-flex
                                    items-center
                                    gap-2
                                    rounded-full
                                    border
                                    border-[#E0D7C8]
                                    bg-white
                                    px-3
                                    sm:px-4
                                    py-2
                                    mb-5
                                    lg:mb-7
                                    text-[10px]
                                    sm:text-xs
                                    font-semibold
                                    tracking-[0.14em]
                                    uppercase
                                    text-[#77718E]
                                "
                            >
                                <span className="w-2 h-2 rounded-full bg-[#4035C9]" />
                                Teacher portal
                            </div>

                            <h1
                                className="
                                    text-4xl
                                    sm:text-5xl
                                    md:text-6xl
                                    lg:text-6xl
                                    xl:text-7xl
                                    leading-tight
                                    font-bold
                                    tracking-tight
                                    text-[#211C57]
                                "
                            >
                                Teacher login
                            </h1>

                            <p
                                className="
                                    mt-5
                                    sm:mt-6
                                    max-w-[580px]
                                    mx-auto
                                    lg:mx-0
                                    text-base
                                    sm:text-lg
                                    md:text-xl
                                    leading-7
                                    sm:leading-8
                                    text-[#706987]
                                "
                            >
                                Your timetable, your batches,
                                your students' gaps. Everything
                                you need before the bell.
                            </p>

                            {/* =================================================
                                TEACHER FEATURES
                            ================================================= */}
                            <div
                                className="
                                    mt-7
                                    sm:mt-9
                                    grid
                                    grid-cols-1
                                    sm:grid-cols-3
                                    gap-3
                                    sm:gap-4
                                    max-w-[620px]
                                    mx-auto
                                    lg:mx-0
                                "
                            >

                                <div
                                    className="
                                        rounded-xl
                                        border
                                        border-[#E3D9C8]
                                        bg-white
                                        px-4
                                        py-4
                                        text-left
                                    "
                                >
                                    <UserRound
                                        size={20}
                                        className="text-[#4035C9]"
                                    />

                                    <p
                                        className="
                                            mt-3
                                            text-sm
                                            font-bold
                                            text-[#211C57]
                                        "
                                    >
                                        Manage batches
                                    </p>

                                    <p
                                        className="
                                            mt-1
                                            text-xs
                                            leading-5
                                            text-[#77718E]
                                        "
                                    >
                                        Keep your classes and
                                        students organised.
                                    </p>
                                </div>

                                <div
                                    className="
                                        rounded-xl
                                        border
                                        border-[#E3D9C8]
                                        bg-white
                                        px-4
                                        py-4
                                        text-left
                                    "
                                >
                                    <LockKeyhole
                                        size={20}
                                        className="text-[#4035C9]"
                                    />

                                    <p
                                        className="
                                            mt-3
                                            text-sm
                                            font-bold
                                            text-[#211C57]
                                        "
                                    >
                                        Secure access
                                    </p>

                                    <p
                                        className="
                                            mt-1
                                            text-xs
                                            leading-5
                                            text-[#77718E]
                                        "
                                    >
                                        Sign in securely to your
                                        teacher account.
                                    </p>
                                </div>

                                <div
                                    className="
                                        rounded-xl
                                        border
                                        border-[#E3D9C8]
                                        bg-white
                                        px-4
                                        py-4
                                        text-left
                                    "
                                >
                                    <ArrowRight
                                        size={20}
                                        className="text-[#4035C9]"
                                    />

                                    <p
                                        className="
                                            mt-3
                                            text-sm
                                            font-bold
                                            text-[#211C57]
                                        "
                                    >
                                        Quick access
                                    </p>

                                    <p
                                        className="
                                            mt-1
                                            text-xs
                                            leading-5
                                            text-[#77718E]
                                        "
                                    >
                                        Reach your teacher
                                        dashboard quickly.
                                    </p>
                                </div>

                            </div>
                        </div>

                        {/* =================================================
                            LOGIN CARD
                        ================================================= */}
                        <div
                            className="
                                w-full
                                max-w-[620px]
                                mx-auto
                                lg:mx-0
                                lg:ml-auto
                                order-1
                                lg:order-2
                            "
                        >

                            <div
                                className="
                                    w-full
                                    bg-white
                                    border
                                    border-[#E3D9C8]
                                    rounded-2xl
                                    sm:rounded-[22px]
                                    shadow-[0_12px_45px_rgba(37,29,82,0.08)]
                                    p-5
                                    sm:p-7
                                    md:p-8
                                    lg:p-9
                                "
                            >

                                {/* CARD HEADER */}
                                <div
                                    className="
                                        flex
                                        flex-col
                                        sm:flex-row
                                        sm:items-start
                                        sm:justify-between
                                        gap-3
                                    "
                                >
                                    <div>
                                        <p
                                            className="
                                                text-[10px]
                                                sm:text-xs
                                                font-semibold
                                                tracking-[0.16em]
                                                uppercase
                                                text-[#89829F]
                                            "
                                        >
                                            Teacher access
                                        </p>

                                        <h2
                                            className="
                                                mt-1
                                                text-2xl
                                                sm:text-3xl
                                                md:text-4xl
                                                font-bold
                                                text-[#211C57]
                                            "
                                        >
                                            Sign in
                                        </h2>
                                    </div>

                                    <Link
                                        to="/login"
                                        className="
                                            text-sm
                                            sm:text-base
                                            text-[#4035C9]
                                            hover:underline
                                            whitespace-nowrap
                                        "
                                    >
                                        I'm a student
                                    </Link>
                                </div>

                                {/* ERROR */}
                                {error && (
                                    <div
                                        className="
                                            mt-5
                                            rounded-xl
                                            border
                                            border-[#F0C9C1]
                                            border-l-4
                                            border-l-[#D94B32]
                                            bg-[#FCEDEA]
                                            px-4
                                            py-3
                                            text-sm
                                            leading-5
                                            text-[#7C3025]
                                        "
                                    >
                                        {error}
                                    </div>
                                )}

                                {/* FORM */}
                                <form
                                    onSubmit={handleLogin}
                                    className="mt-7 sm:mt-8"
                                >

                                    {/* USERNAME */}
                                    <div>
                                        <label
                                            htmlFor="teacher-username"
                                            className="
                                                block
                                                mb-2
                                                text-sm
                                                sm:text-base
                                                font-medium
                                                text-[#40396A]
                                            "
                                        >
                                            Username, email or mobile
                                        </label>

                                        <div className="relative">
                                            <UserRound
                                                size={19}
                                                className="
                                                    absolute
                                                    left-4
                                                    top-1/2
                                                    -translate-y-1/2
                                                    text-[#8B849F]
                                                    pointer-events-none
                                                "
                                            />

                                            <input
                                                id="teacher-username"
                                                type="text"
                                                value={form.username}
                                                onChange={(e) =>
                                                    handleChange(
                                                        "username",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Enter user name"
                                                autoComplete="username"
                                                className="
                                                    w-full
                                                    h-12
                                                    sm:h-14
                                                    rounded-xl
                                                    border
                                                    border-[#DDCFB8]
                                                    bg-white
                                                    pl-12
                                                    pr-4
                                                    text-base
                                                    sm:text-lg
                                                    text-[#211C57]
                                                    placeholder:text-[#A29BB4]
                                                    outline-none
                                                    transition
                                                    focus:border-[#4035C9]
                                                    focus:ring-2
                                                    focus:ring-[#4035C9]/10
                                                "
                                            />
                                        </div>
                                    </div>

                                    {/* PASSWORD */}
                                    <div className="mt-5 sm:mt-6">
                                        <label
                                            htmlFor="teacher-password"
                                            className="
                                                block
                                                mb-2
                                                text-sm
                                                sm:text-base
                                                font-medium
                                                text-[#40396A]
                                            "
                                        >
                                            Password
                                        </label>

                                        <div className="relative">
                                            <LockKeyhole
                                                size={19}
                                                className="
                                                    absolute
                                                    left-4
                                                    top-1/2
                                                    -translate-y-1/2
                                                    text-[#8B849F]
                                                    pointer-events-none
                                                "
                                            />

                                            <input
                                                id="teacher-password"
                                                type={
                                                    showPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                value={form.password}
                                                onChange={(e) =>
                                                    handleChange(
                                                        "password",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Enter password"
                                                autoComplete="current-password"
                                                className="
                                                    w-full
                                                    h-12
                                                    sm:h-14
                                                    rounded-xl
                                                    border
                                                    border-[#DDCFB8]
                                                    bg-white
                                                    pl-12
                                                    pr-12
                                                    text-base
                                                    sm:text-lg
                                                    text-[#211C57]
                                                    placeholder:text-[#A29BB4]
                                                    outline-none
                                                    transition
                                                    focus:border-[#4035C9]
                                                    focus:ring-2
                                                    focus:ring-[#4035C9]/10
                                                "
                                            />

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPassword(
                                                        (prev) => !prev
                                                    )
                                                }
                                                aria-label={
                                                    showPassword
                                                        ? "Hide password"
                                                        : "Show password"
                                                }
                                                className="
                                                    absolute
                                                    right-3
                                                    sm:right-4
                                                    top-1/2
                                                    -translate-y-1/2
                                                    p-2
                                                    text-[#77718E]
                                                    hover:text-[#4035C9]
                                                    transition
                                                "
                                            >
                                                {showPassword ? (
                                                    <EyeOff size={20} />
                                                ) : (
                                                    <Eye size={20} />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* OPTIONS */}
                                    <div
                                        className="
                                            mt-4
                                            sm:mt-5
                                            flex
                                            flex-col
                                            sm:flex-row
                                            sm:items-center
                                            sm:justify-between
                                            gap-3
                                        "
                                    >
                                        <label
                                            className="
                                                flex
                                                items-center
                                                gap-2
                                                cursor-pointer
                                                text-sm
                                                sm:text-base
                                                text-[#40396A]
                                            "
                                        >
                                            <input
                                                type="checkbox"
                                                checked={staySignedIn}
                                                onChange={(e) =>
                                                    setStaySignedIn(
                                                        e.target.checked
                                                    )
                                                }
                                                className="
                                                    w-4
                                                    h-4
                                                    accent-[#4035C9]
                                                "
                                            />

                                            <span>
                                                Stay signed in
                                            </span>
                                        </label>

                                        <Link
                                            to="/forget-password"
                                            className="
                                                text-sm
                                                sm:text-base
                                                text-[#4035C9]
                                                hover:underline
                                            "
                                        >
                                            Forgot password?
                                        </Link>
                                    </div>

                                    {/* MAIN BUTTONS */}
                                    <div
                                        className="
                                            mt-6
                                            flex
                                            flex-col
                                            sm:flex-row
                                            gap-3
                                        "
                                    >
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="
                                                w-full
                                                sm:w-auto
                                                min-w-[120px]
                                                h-12
                                                sm:h-14
                                                px-6
                                                rounded-full
                                                bg-[#211C57]
                                                text-white
                                                text-base
                                                sm:text-lg
                                                font-semibold
                                                hover:bg-[#171440]
                                                transition
                                                disabled:opacity-60
                                                disabled:cursor-not-allowed
                                            "
                                        >
                                            {loading
                                                ? "Signing in..."
                                                : "Sign in"}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={fillDemoDetails}
                                            className="
                                                w-full
                                                sm:w-auto
                                                h-12
                                                sm:h-14
                                                px-6
                                                rounded-full
                                                border
                                                border-[#DDCFB8]
                                                bg-white
                                                text-[#211C57]
                                                text-base
                                                sm:text-lg
                                                font-medium
                                                hover:bg-[#FAF7F1]
                                                transition
                                            "
                                        >
                                            Fill demo details
                                        </button>
                                    </div>

                                    {/* DIVIDER */}
                                    <div
                                        className="
                                            my-6
                                            sm:my-7
                                            h-px
                                            bg-[#E7DDCE]
                                        "
                                    />

                                    {/* OTP */}
                                    <Link
                                        to="/login-with-otp"
                                        className="
                                            w-full
                                            min-h-12
                                            sm:h-12
                                            px-4
                                            rounded-full
                                            border
                                            border-[#DDCFB8]
                                            bg-white
                                            text-[#211C57]
                                            flex
                                            items-center
                                            justify-center
                                            gap-2
                                            text-sm
                                            sm:text-base
                                            font-medium
                                            hover:bg-[#FAF7F1]
                                            hover:border-[#4035C9]
                                            transition
                                        "
                                    >
                                        <LockKeyhole
                                            size={18}
                                        />

                                        <span>
                                            Login via OTP
                                        </span>
                                    </Link>

                                    {/* STUDENT LOGIN */}
                                    <Link
                                        to="/login"
                                        className="
                                            mt-3
                                            w-full
                                            min-h-12
                                            sm:h-12
                                            px-4
                                            rounded-full
                                            border
                                            border-[#DDCFB8]
                                            bg-white
                                            text-[#211C57]
                                            flex
                                            items-center
                                            justify-center
                                            gap-2
                                            text-sm
                                            sm:text-base
                                            font-medium
                                            hover:bg-[#FAF7F1]
                                            hover:border-[#4035C9]
                                            transition
                                        "
                                    >
                                        <span>
                                            I'm a student
                                        </span>

                                        <ArrowRight
                                            size={18}
                                        />
                                    </Link>

                                </form>
                            </div>
                        </div>

                    </section>
                </div>
            </main>

            {/* =====================================================
                FOOTER
            ===================================================== */}
            <footer
                className="
                    w-full
                    px-4
                    sm:px-6
                    md:px-8
                    lg:px-10
                    pb-6
                    sm:pb-7
                "
            >
                <div
                    className="
                        w-full
                        max-w-[1380px]
                        mx-auto
                        border-t
                        border-[#E5DAC7]
                        pt-6
                        sm:pt-7
                        flex
                        items-center
                        justify-center
                        gap-4
                        sm:gap-6
                        flex-wrap
                        text-xs
                        sm:text-sm
                        text-[#6E6990]
                    "
                >
                    <Link
                        to="/terms-condition"
                        className="hover:text-[#4035C9] transition"
                    >
                        Terms of use
                    </Link>

                    <Link
                        to="/policy"
                        className="hover:text-[#4035C9] transition"
                    >
                        Privacy policy
                    </Link>

                    <span>
                        © 2026
                    </span>
                </div>
            </footer>

        </div>
    );
};

export default TeacherLoginPage;